import { createContext, useContext } from "react";
import { ExtractUpdateEntry, UndoRedoEntry, UndoRedoV2 } from "@iventis/utilities/src/undo-redo/undo-redo-v2";
import { UndoRedoContextType } from "@iventis/utilities/src/undo-redo/undo-redo-v2-context";
import { ObjectUpdate } from "@iventis/map-engine/src/types/events-store";
import { extractChangeInGeoJson } from "@iventis/map-engine/src/utilities/undo-redo-helpers";
import isEqual from "lodash.isequal";
import { MapObjectProperties } from "@iventis/map-types/src/map-object-properties";
import { OptionalExceptFor } from "@iventis/types/useful.types";
import { SelectedMapObject } from "@iventis/map-types/src/map-object-types";

/** The partial undoable item for map object changes */
export interface PartialObjectUpdate extends OptionalExceptFor<Omit<ObjectUpdate, "properties">, "objectId" | "layerId"> {
    properties?: Partial<MapObjectProperties>;
}

/** An undo/redo entry for map objects */
export type MapObjectUndoRedoEntry = UndoRedoEntry<PartialObjectUpdate, ObjectUpdate> & {
    type: "mapObject";
};

/** An undo/redo entry for map object selection */
export type MapObjectSelectionUndoRedoEntry = UndoRedoEntry<SelectedMapObject[]> & {
    type: "mapObjectSelection";
};

/** Combined union of the partial map-related undo/redo items */
export type MapUndoRedoItemPartial = PartialObjectUpdate | SelectedMapObject[];

/** Combined union of the map-related undo/redo items */
export type MapUndoRedoItem = ObjectUpdate | SelectedMapObject[];

export type MapUndoRedo = UndoRedoV2<MapUndoRedoItemPartial, MapUndoRedoItem, MapObjectUndoRedoEntry | MapObjectSelectionUndoRedoEntry>;

export interface MapUndoRedoContextType extends UndoRedoContextType, Pick<MapUndoRedo, "push" | "removeFromStack"> {
    /** Tell the undo/redo store that objects have been deleted */
    registerDelete: (ids: { objectId: string; layerId: string }[]) => void;
}

/** Map-related undo/redo context */
export const MapUndoRedoContext = createContext<MapUndoRedoContextType | undefined>(undefined);

/** A hook to access the map-related undo/redo context */
export const useMapUndoRedoContext = () => useContext(MapUndoRedoContext);

export type MapObjectUndoRedoChange = ExtractUpdateEntry<MapObjectUndoRedoEntry>["change"][number];
export type MapObjectSelectionUndoRedoChange = ExtractUpdateEntry<MapObjectSelectionUndoRedoEntry>["change"][number];

/** Takes the previous state of a map object and the next state of a map object and returns the changed properties */
export const exctractChangeInObjectUpdate = (
    previous: ObjectUpdate | undefined,
    next: ObjectUpdate,
    getUndoableProperties: () => (keyof MapObjectProperties)[]
): MapObjectUndoRedoChange | "equal" | undefined => {
    // If eitrher the previous value or the next value are not defined, then this is not a change
    if (!previous || !next) {
        return undefined;
    }

    const change: MapObjectUndoRedoChange = {
        from: { objectId: previous.objectId, layerId: previous.layerId },
        to: { objectId: next.objectId, layerId: next.layerId },
    };

    const changeInGeoJson = extractChangeInGeoJson(previous, next, getUndoableProperties);
    change.from.geometry = changeInGeoJson.from.geometry;
    change.from.properties = changeInGeoJson.from.properties;
    change.to.geometry = changeInGeoJson.to.geometry;
    change.to.properties = changeInGeoJson.to.properties;

    if (!isEqual(previous.waypoints, next.waypoints)) {
        change.from.waypoints = previous.waypoints;
        change.to.waypoints = next.waypoints;
    }

    if (isEqual(change.from, change.to)) {
        return "equal";
    }

    return change;
};

/**
 * A reduce callback function for filtering out undo/redo entries or individual changes within an entry that don't involve the given layers
 * @param accumulatedStack Resulting undo or redo stack
 * @param entry The undo/redo entry
 */
export const removeLayerObjectsFromStack = (
    accumulatedStack: (MapObjectUndoRedoEntry | MapObjectSelectionUndoRedoEntry)[],
    entry: MapObjectUndoRedoEntry | MapObjectSelectionUndoRedoEntry,
    layerIds: string[]
) => {
    // We only handle selection updates; you can't create or delete a selection
    if (entry.type === "mapObjectSelection" && entry.operation === "update") {
        const change = entry.change.reduce<MapObjectSelectionUndoRedoChange[]>((accumulatedChange, currentChange) => {
            // Filter out the layers we want to remove
            const from = currentChange.from.filter(({ layerId }) => !layerIds.includes(layerId));
            const to = currentChange.to.filter(({ layerId }) => !layerIds.includes(layerId));
            // If the selection change involves _only_ the layers we want to remove, we can skip it
            if (!from.length && !to.length) {
                return accumulatedChange;
            }
            // Otherwise, we need to keep the change but filter out the layers we don't want
            return [...accumulatedChange, { from, to }];
        }, []);

        // If we aren't left with any changes, we can skip the entry
        if (change.length) {
            accumulatedStack.push({ ...entry, change });
        }
        return accumulatedStack;
    }
    if (entry.type === "mapObject") {
        if (entry.operation === "create") {
            const items = entry.items.filter(({ layerId }) => !layerIds.includes(layerId));
            if (items.length) {
                accumulatedStack.push({ ...entry, operation: "create", items });
            }
        }
        if (entry.operation === "delete") {
            const items = entry.items.filter(({ layerId }) => !layerIds.includes(layerId));
            if (items.length) {
                accumulatedStack.push({ ...entry, operation: "delete", items });
            }
        }
        if (entry.operation === "update") {
            const changes = entry.change.reduce<MapObjectUndoRedoChange[]>((accumulatedChange, currentChange) => {
                if (layerIds.includes(currentChange.from.layerId) || layerIds.includes(currentChange.to.layerId)) {
                    return accumulatedChange;
                }
                return [...accumulatedChange, currentChange];
            }, []);
            if (changes.length) {
                accumulatedStack.push({ ...entry, operation: "update", change: changes });
            }
        }
        return accumulatedStack;
    }

    // We don't handle other types of map object selection, so if it reaches here, we can just ignore it
    if (entry.type === "mapObjectSelection") {
        return accumulatedStack;
    }

    // @ts-expect-error - This will catch any unhandled types at compile time
    throw new Error(`Invalid undo/redo entry type: ${entry.type}`);
};
